import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

interface User {
  password: string;
  email: string;
}

interface LoginRequest {
  username: string;
  password: string;
}

interface LoginResponse {
  access_token: string;
  token_type: string;
}

interface RegisterResponse {
  id: string;
  password: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
}

interface TelegramAccessCode {
  tg_id?: number;
  tg_username?: string;
}

interface TelegramCheckCode {
  tg_id?: number;
  tg_username?: string;
  access_code: string;
}

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_FLAT_API,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Auth'],

  endpoints: (builder) => ({
    register: builder.mutation<RegisterResponse, { user: User }>({
      query: ({ user }) => ({
        url: "auth/register",
        method: "POST",
        body: user,
      }),
    }),

    verifyAccount: builder.mutation({
      query: ({ data }) => ({
        url: "/auth/request-verify-token",
        method: "POST",
        body: data,
      }),
    }),

    verify: builder.mutation({
      query: ({ data }) => ({
        url: "/auth/verify",
        method: "POST",
        body: data,
        headers: {
          'Content-Type': 'application/json'
        }
      }),
    }),

    login: builder.mutation({
      query: ({ user }) => ({
        url: "auth/jwt/login",
        method: "POST",
        body: user,
      }),
    }),

    forgotPassword: builder.mutation({
      query: ({ data }) => ({
        url: "auth/forgot-password",
        method: "POST",
        body: data,
      }),
    }),

    resetPassword: builder.mutation({
      query: ({ data }) => ({
        url: "auth/reset-password",
        method: "POST",
        body: data,
      }),
    }),
    
    payment: builder.mutation({
      query: ({ data }) => ({
        url: "payments/link",
        method: "POST",
        body: data,
      }),
    }),

    logout: builder.mutation({
      query: () => ({
        url: "auth/jwt/logout",
        method: "POST",
      }),
    }),

    editUserInfo: builder.mutation({
      query: ({ user }) => ({
        url: "user_infos/me",
        method: "PATCH",
        body: user,
      }),
    }),

    editUser: builder.mutation({
      query: ({ user }) => ({
        url: "users/me",
        method: "PATCH",
        body: user,
      }),
    }),

    getUser: builder.query({
      query: () => "/user_infos/me",
    }),

    getUserEmail: builder.query({
      query: () => "/users/me",
    }),
    
    getUserById: builder.query({
      query: ({ id }) => ({
        url: `/user_infos/${id}`,
        headers: {
          'Accept-Language': 'en'
        }
      }),
    }),

    getUserEmailById: builder.query({
      query: ({ id }) => ({
        url: `/users/by_id/${id}`,
      }),
    }),

    updateUserPicture: builder.mutation({
      query: ({ data }) => {
        return {
          url: "/user_infos/update_my_picture",
          method: "PATCH",
          body: data,
        };
      },
    }),
    
    createUserInfo: builder.mutation({
      query: ({ data }) => ({
        url: "/user_infos/me",
        method: "POST",
        body: data,
      }),
    }),

    userAccessRequest: builder.mutation({
      query: ({ data }) => ({
        url: "/user_access_requests",
        method: "POST",
        body: data,
      }),
    }),

    // telegramLoginRequest: builder.mutation<any, { user: TelegramAccessCode }>({
    //   query: ({ user }) => ({
    //     url: "auth/telegram/request-code",
    //     method: "POST",
    //     body: user,
    //   }),
    // }),

    telegramCodeCheck: builder.mutation<LoginResponse, { tgCheckCode: TelegramCheckCode }>({
      query: ({ tgCheckCode }) => ({
        url: "auth/telegram/login",
        method: "POST",
        body: tgCheckCode,
      }),
    }),

  }),
});

export const updateAuthApiToken = () => {
  authApi.util.resetApiState();
};

export const {
  useRegisterMutation,
  useLoginMutation,
  useVerifyAccountMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useEditUserInfoMutation,
  useGetUserQuery,
  useGetUserByIdQuery,
  useGetUserEmailByIdQuery,
  useUpdateUserPictureMutation,
  useLogoutMutation,
  useGetUserEmailQuery,
  useEditUserMutation,
  usePaymentMutation,
  useVerifyMutation,
  useCreateUserInfoMutation,
  useUserAccessRequestMutation,
  // useTelegramLoginRequestMutation,
  useTelegramCodeCheckMutation,
} = authApi;
